import React from "react";
import { FieldArray } from "react-final-form-arrays";
import { Field } from "react-final-form";
import {
  InstrumentTypeSelectField,
  ClarinetMouthpieceChamberTypeSelectField,
  MouthpieceBaffleTypeSelectField,
} from "@syos-music/instruments";
import { Fieldset, TextField } from "@syos-music/form";

import { Grid, Typography, Collapse, InputAdornment } from "@mui/material";

import { clarinet as clarinetMouthpieceUtils } from "components/mouthpiece/utils";

const floatFieldProps = {
  parse: (value) => {
    const result = parseFloat(value);
    return isNaN(result) ? value : result;
  },
};

const integerFieldProps = {
  parse: (value) => {
    const result = parseInt(value, 10);
    return isNaN(result) ? value : result;
  },
};

const safe =
  (fn) =>
  (...args) => {
    try {
      return fn(...args);
    } catch (err) {
      // console.error(err);
      return null;
    }
  };

function ClarinetMouthpieceField({ name }) {
  return (
    <Field name={name}>
      {({ input: { value, onChange } }) => {
        const tipOneningRange = value.clarinetType
          ? clarinetMouthpieceUtils.getTipOpeningRange(value.clarinetType)
          : [];

        return (
          <Grid container alignContent="flex-start">
            <Grid item xs={12}>
              <InstrumentTypeSelectField
                instrument="clarinet"
                name={`${name}.clarinetType`}
                required
                onChange={(evt) => {
                  const clarinetType = evt.target.value;
                  if (clarinetType === value.clarinetType) return;

                  const defaults =
                    clarinetMouthpieceUtils.applyMouthpieceDefaults({
                      clarinetType,
                    });

                  onChange(defaults);
                }}
                margin="normal"
                validate={(clarinetType) => {
                  if (
                    !clarinetMouthpieceUtils.isValidClarinetType(clarinetType)
                  )
                    return "Type de clarinette invalide";
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Collapse in={!!value.clarinetType}>
                <Grid container>
                  <Grid item xs={12}>
                    <Fieldset legend="Plafond" margin="normal">
                      <Grid container>
                        <Grid item xs={12}>
                          <MouthpieceBaffleTypeSelectField
                            instrument="clarinet"
                            instrumentType={value.clarinetType}
                            name={`${name}.baffle.type`}
                            required
                            validate={(baffleType) => {
                              if (
                                !clarinetMouthpieceUtils.isValidBaffleType(
                                  baffleType,
                                  value.clarinetType
                                )
                              )
                                return "Type de plafond invalide";
                            }}
                            onChange={(evt) => {
                              const baffleType = evt.target.value;

                              let newValue = {
                                ...value,
                                baffle: {
                                  ...value.baffle,
                                  type: baffleType,
                                },
                              };

                              if (
                                baffleType !==
                                (value.baffle && value.baffle.type)
                              ) {
                                newValue.baffle = newValue.baffle || {};
                                newValue.baffle.params =
                                  clarinetMouthpieceUtils.getDefaultBaffleParams(
                                    baffleType,
                                    value.clarinetType
                                  );
                              }

                              onChange(newValue);
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FieldArray name={`${name}.baffle.params`}>
                            {({ fields, ...rest }) =>
                              !!fields.length && (
                                <Fieldset
                                  legend="Paramètre(s) du plafond"
                                  margin="normal"
                                  sx={{
                                    ...(!value.baffle?.params?.length
                                      ? {
                                          paddingTop: 0,
                                          paddingBottom: 0,
                                          borderBottom: "none",
                                          borderLeft: "none",
                                          borderRight: "none",
                                        }
                                      : null),
                                  }}
                                >
                                  <Grid container>
                                    {fields.map((fieldName, idx) => (
                                      <Grid
                                        key={fieldName}
                                        item
                                        xs={12}
                                        sm={12 / fields.length}
                                      >
                                        <TextField
                                          type="number"
                                          name={fieldName}
                                          label={`Param #${idx + 1}`}
                                          {...floatFieldProps}
                                          InputProps={{
                                            inputProps: { step: 0.1 },
                                            endAdornment: (
                                              <InputAdornment position="end">
                                                mm
                                              </InputAdornment>
                                            ),
                                          }}
                                          validate={(baffleParam) => {
                                            if (
                                              !clarinetMouthpieceUtils.isValidBaffleParam(
                                                baffleParam
                                              )
                                            )
                                              return "Paramètre invalide";
                                          }}
                                        />
                                      </Grid>
                                    ))}
                                  </Grid>
                                </Fieldset>
                              )
                            }
                          </FieldArray>
                        </Grid>
                      </Grid>{" "}
                    </Fieldset>
                  </Grid>

                  <Grid item xs={12}>
                    <Fieldset legend="Chambre" margin="normal">
                      <Grid container>
                        <Grid item xs={12}>
                          <ClarinetMouthpieceChamberTypeSelectField
                            clarinetType={value.clarinetType}
                            name={`${name}.chamber.type`}
                            required
                            onChange={(evt) => {
                              const chamberType = evt.target.value;

                              let newValue = {
                                ...value,
                                chamber: {
                                  ...value.chamber,
                                  type: chamberType,
                                },
                              };

                              if (
                                chamberType !==
                                (value.chamber && value.chamber.type)
                              ) {
                                newValue.chamber = newValue.chamber || {};
                                newValue.chamber.params =
                                  clarinetMouthpieceUtils.getDefaultChamberParams(
                                    chamberType,
                                    value.clarinetType
                                  );
                              }

                              onChange(newValue);
                            }}
                            validate={(chamberType) => {
                              if (
                                !clarinetMouthpieceUtils.isValidChamberType(
                                  chamberType,
                                  value.clarinetType
                                )
                              )
                                return "Type de chambre invalide";
                            }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FieldArray name={`${name}.chamber.params`}>
                            {({ fields, ...rest }) =>
                              !!fields.length && (
                                <Fieldset
                                  legend="Paramètre(s) de la chambre"
                                  margin="normal"
                                  sx={{
                                    ...(!value.chamber?.params?.length
                                      ? {
                                          paddingTop: 0,
                                          paddingBottom: 0,
                                          borderBottom: "none",
                                          borderLeft: "none",
                                          borderRight: "none",
                                        }
                                      : null),
                                  }}
                                >
                                  <Grid container>
                                    {fields.map((fieldName, idx) => {
                                      const range =
                                        clarinetMouthpieceUtils.getChamberParamsRanges(
                                          value.chamber.type,
                                          value.clarinetType
                                        )[idx];

                                      return (
                                        <Grid
                                          key={fieldName}
                                          item
                                          xs={12}
                                          sm={12 / fields.length}
                                        >
                                          <TextField
                                            type="number"
                                            name={fieldName}
                                            label={`Param #${idx + 1}`}
                                            {...floatFieldProps}
                                            InputProps={{
                                              inputProps: {
                                                step: 0.1,
                                                min: range[0],
                                                max: range[1],
                                              },
                                              endAdornment: (
                                                <InputAdornment position="end">
                                                  mm
                                                </InputAdornment>
                                              ),
                                            }}
                                            validate={(baffleParam) => {
                                              if (
                                                !clarinetMouthpieceUtils.isValidBaffleParam(
                                                  baffleParam
                                                )
                                              )
                                                return "Paramètre invalide";
                                            }}
                                          />
                                        </Grid>
                                      );
                                    })}
                                  </Grid>
                                </Fieldset>
                              )
                            }
                          </FieldArray>
                        </Grid>
                      </Grid>
                    </Fieldset>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      name={`${name}.striations`}
                      type="number"
                      required
                      {...integerFieldProps}
                      label="Stries"
                      margin="normal"
                      inputProps={{ step: 1, min: 0 }}
                      validate={(striations) => {
                        if (
                          !clarinetMouthpieceUtils.isValidStriations(
                            striations,
                            value.baffle && value.baffle.type,
                            value.clarinetType
                          )
                        )
                          return "Nombre de stries invalide";
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      name={`${name}.tipOpening`}
                      type="number"
                      required
                      {...integerFieldProps}
                      label="Ouverture"
                      margin="normal"
                      InputProps={{
                        inputProps: {
                          step: 1,
                          min: tipOneningRange[0],
                          max: tipOneningRange[1],
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      name={`${name}.facing`}
                      type="number"
                      required
                      {...integerFieldProps}
                      label="Facing"
                      margin="normal"
                      InputProps={{
                        inputProps: { step: 1, min: 1 },
                        endAdornment: (
                          <InputAdornment position="end">mm</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      name={`${name}.boreDiameter`}
                      type="number"
                      required
                      {...floatFieldProps}
                      label="Diamètre d’entrée (perce)"
                      margin="normal"
                      InputProps={{
                        inputProps: { step: 0.1 },
                        endAdornment: (
                          <InputAdornment position="end">mm</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography
                      variant="h1"
                      sx={{
                        margin: (t) => t.spacing(2, 0),
                        fontFamily: "'Roboto Mono', monospace",
                      }}
                    >
                      {safe(clarinetMouthpieceUtils.getNomenclature)(value)}
                    </Typography>
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>
          </Grid>
        );
      }}
    </Field>
  );
}

ClarinetMouthpieceField.defaultValues = {
  ...clarinetMouthpieceUtils.applyMouthpieceDefaults({
    // clarinetType: "tenor",
    // baffle: {
    //   type: "P5",
    // },
    // chamber: {
    //   type: "-",
    //   sizeName: "N",
    //   modifier: 35,
    // },
    // tipOpening: 267,
  }),
};

export default ClarinetMouthpieceField;
